import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import NewsletterForm from '@/components/feature/NewsletterForm';
import { useSeo } from '@/hooks/useSeo';
import { brandImage, siteStats, testimonials } from '@/mocks/home';
import { CONTACT_EMAIL, SITE_INTRO, SITE_NAME, SITE_ORIGIN } from '@/lib/site';
import { btn } from '@/lib/ui';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '信息真实优先',
    desc: '热点快速跟进，同时保留事实核对的时间，不用未经确认的说法换取短期热度。',
  },
  {
    icon: 'ri-hd-line',
    title: '画质不做妥协',
    desc: '宁可清晰度保守，也不过度插值失真。标注的清晰度，会和观众实际观感一致。',
  },
  {
    icon: 'ri-cpu-line',
    title: '体验持续优化',
    desc: '把加载速度、缓冲次数、线路稳定性当成核心指标，每次版本都会盯住这些数字。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '尊重观看者',
    desc: '不打断播放强制跳转，不用诱导性标题制造焦虑，把选择权交回给你。',
  },
];

const milestones = [
  { year: '2020', title: '起步', desc: '从一个小小的内容合集开始，最初只有几十条视频。' },
  { year: '2022', title: '自建播放线路', desc: '开始自建多线路调度，缓冲次数显著下降，观看体验稳定下来。' },
  { year: '2024', title: '全站画质增强', desc: '上线统一画质增强流程，暗部细节与夜景表现明显提升。' },
  { year: '2026', title: '多端续播', desc: '打通手机、平板与电脑的观看进度，换设备也能接着看。' },
];

const speedHighlights = [
  { icon: 'ri-flashlight-line', title: '秒开加载', desc: '预加载策略让首帧时间稳定在 0.3 秒以内。' },
  { icon: 'ri-hd-line', title: '超清画质', desc: '最高支持 4K，暗部与夜景细节都保留下来。' },
  { icon: 'ri-route-line', title: '多线路调度', desc: '自动选择更稳定的线路，弱网也能顺畅播放。' },
  { icon: 'ri-device-line', title: '多端续播', desc: '手机、平板、电脑进度同步，换设备接着看。' },
];

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${SITE_NAME}`,
    url: `${SITE_ORIGIN}/about`,
    inLanguage: 'zh-CN',
    description: SITE_INTRO,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    email: CONTACT_EMAIL,
    description: SITE_INTRO,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_ORIGIN}/about` },
    ],
  },
];

export default function About() {
  useSeo({
    title: `关于我们 - 看料社品牌介绍与内容原则`,
    description:
      '了解看料社：一个专注热门内容聚合与播放体验优化的团队。我们坚持信息真实、画质不做妥协、体验持续优化的内容原则，为用户提供在线看料高清不卡顿的观看体验。',
    keywords: '关于看料社,在线看料,看料社品牌介绍,内容原则,短视频聚合平台',
    path: '/about',
    jsonLd: aboutJsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100 pt-24 md:pt-32">
        <div className="mx-auto w-full max-w-shell px-4 py-12 md:px-8 md:py-16">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs font-label text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="mt-4 max-w-3xl font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-4xl">
            关于看料社：把顺畅地看见，当成一件正经事
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {SITE_INTRO}
          </p>
        </div>
      </div>

      <section id="intro" className="py-16 md:py-24">
        <div className="mx-auto w-full max-w-shell px-4 md:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <SectionHeading
                kicker="品牌介绍"
                title="一个专注内容聚合与播放优化的小团队"
                desc="我们没有庞大的编辑部，只有一群喜欢看现场、也挑剔播放体验的人。与其堆量，我们更愿意把每一条内容做得经得起回看。"
              />
              <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                从最早的内容合集，到自建多线路调度，再到统一的画质增强流程，看料社的每一次迭代都围绕同一个问题：怎样让人少等一点、多看一点。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-background-200 pt-8 md:grid-cols-4">
                {siteStats.map((stat) => (
                  <div key={stat.label}>
                    <p className="font-heading text-2xl font-semibold text-primary-600 md:text-3xl">
                      {stat.value}
                      <span className="ml-0.5 text-sm font-normal text-foreground-500 md:text-base">{stat.unit}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-500 md:text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="h-72 w-full overflow-hidden rounded-lg md:h-[440px]">
                <img
                  src={brandImage}
                  alt="看料社内容团队的工作空间，专注内容质量与播放体验"
                  title="看料社 - 内容聚合与播放优化团队"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="position" className="bg-foreground-950 py-16 md:py-20">
        <div className="mx-auto w-full max-w-shell px-4 md:px-8">
          <Reveal>
            <SectionHeading
              tone="dark"
              kicker="网站定位"
              title="让热点内容的观看，回到「顺畅」本身"
              desc="看料社不做信息堆砌。我们把分类做清楚、把画质做稳定、把速度做扎实，让你打开就能看到想看的那条。"
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {speedHighlights.map((item, index) => (
              <Reveal key={item.title} delay={index * 70}>
                <div className="h-full rounded-lg border border-background-50/15 bg-background-50/5 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-background-50">{item.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-background-300 md:text-sm">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="principles" className="border-t border-background-200 bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-shell px-4 md:px-8">
          <Reveal>
            <SectionHeading
              kicker="内容原则"
              title="我们给自己定的四条规矩"
              desc="这些原则会约束我们怎样选题、怎样标注画质，以及怎样对待每一次播放。"
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 70}>
                <article className="h-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{item.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-4 md:gap-5">
            {milestones.map((item, index) => (
              <Reveal key={item.year} delay={index * 70}>
                <div className="border-t border-background-300 pt-4">
                  <span className="font-heading text-xl font-semibold text-accent-600 md:text-2xl">{item.year}</span>
                  <h3 className="mt-2 text-sm font-medium text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="reviews" className="border-t border-background-200 bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-shell px-4 md:px-8">
          <Reveal>
            <SectionHeading
              kicker="用户口碑"
              title="他们是怎样评价看料社的"
              desc="来自不同职业的观看者，聊了聊他们最在意的那一点。"
            />
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3 md:gap-6">
            {testimonials.map((person, index) => (
              <Reveal key={person.name} delay={index * 90}>
                <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                    <i className="ri-double-quotes-l text-base" />
                  </span>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                    {person.quote}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="h-10 w-10 overflow-hidden rounded-full">
                      <img
                        src={person.avatar}
                        alt={`${person.name}的头像`}
                        title={person.name}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">{person.name}</span>
                      <span className="block text-[11px] text-foreground-500">{person.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-shell px-4 md:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionHeading
                  kicker="联系我们"
                  title="有建议、有内容想推荐，都可以告诉我们"
                  desc="我们会认真读每一条来信。如果内容出现问题，也请第一时间告知，我们会尽快核实与处理。"
                />
                <div className="mt-8 flex flex-col gap-4">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">邮箱联系</span>
                      <span className="block text-sm font-medium text-foreground-950">{CONTACT_EMAIL}</span>
                    </span>
                  </a>
                  <Link
                    to="/category"
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-secondary-300"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-play-list-2-line text-lg" />
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">内容入口</span>
                      <span className="block text-sm font-medium text-foreground-950">浏览全部内容分类</span>
                    </span>
                  </Link>
                </div>
                <Link to="/category" className={`${btn('primary', 'md')} mt-8`}>
                  开始观看
                  <i className="ri-arrow-right-line" />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={100}>
                <div className="rounded-lg border border-background-200 bg-secondary-950 p-6 md:p-8">
                  <NewsletterForm />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}